import React, { useMemo } from 'react';
import { Sparkles } from 'lucide-react';

/**
 * OfficeScene — image-based view of the HST office (siège social) that
 * EVOLVES with the player's progression. 4 phases :
 *
 *   phase 0 : EMPTY shell (locked or unlocked but no upgrades + < 5 support)
 *   phase 1 : basic furniture (3-5 supports + 0-1 upgrade)
 *   phase 2 : enriched (6-10 supports + 2-3 upgrades)
 *   phase 3 : « Classe moderne » (11+ supports OR 4-5 upgrades)
 *
 * Same image pipeline as DepotScene : the 4 PNGs are pre-generated by
 * /app/backend/generate_office_phases.py using Nano Banana iterative
 * editing, then served from /office-scene/office-phase-{N}.webp.
 *
 * Defensive fallback : if the image asset is missing (script not run yet),
 * we render a colored placeholder block — never a broken image icon.
 *
 * 28/05/2026 v2 — User request : "image bureau qui évolue avec le nombre
 * de supports et le nombre d'équipements achetés".
 */

const PHASE_LABELS = [
  'Bureaux vides — espace brut',
  'Bureau de base — mobilier starter',
  'Bureau enrichi — confort installé',
  'Bureau Classe moderne — haut de gamme',
];

/**
 * Compute the phase (0..3) from support count + upgrade count.
 *
 * The formula is intentionally generous : the player should *see* their
 * progression early. So we take the MAX of two paths :
 *   - supports >= 11 OR upgrades >= 4   → phase 3 (modern class)
 *   - supports >= 6  OR upgrades >= 2   → phase 2
 *   - supports >= 3  AND any furniture  → phase 1
 *
 * Returns 0 when locked or when no support/upgrade triggers anything.
 */
export function computeOfficePhase({ unlocked, supportCount, upgradeCount }) {
  if (!unlocked) return 0;
  if (supportCount >= 11 || upgradeCount >= 4) return 3;
  if (supportCount >= 6 || upgradeCount >= 2) return 2;
  if (supportCount >= 3) return 1;
  return 0;
}

export default function OfficeScene({ unlocked, supportCount, upgradeCount }) {
  const phase = useMemo(
    () => computeOfficePhase({ unlocked, supportCount, upgradeCount }),
    [unlocked, supportCount, upgradeCount]
  );
  const src = `/office-scene/office-phase-${phase}.webp`;
  const label = PHASE_LABELS[phase] || PHASE_LABELS[0];

  return (
    <div
      className="relative rounded-md border border-ink-600 overflow-hidden bg-ink-900"
      data-testid="office-scene"
      data-phase={phase}
    >
      <img
        src={src}
        alt={`Siège social — ${label}`}
        loading="lazy"
        className="w-full h-auto block"
        onError={(e) => {
          // Asset missing (generator not run yet) → swap to a CSS placeholder.
          // This keeps the layout intact during dev / first deploy.
          e.currentTarget.style.display = 'none';
          const ph = e.currentTarget.nextElementSibling;
          if (ph) ph.style.display = 'flex';
        }}
      />
      <div
        className="hidden items-center justify-center w-full aspect-[16/9] bg-gradient-to-br from-blue-100 to-amber-100 text-slate-600 text-xs italic"
        data-testid="office-scene-fallback"
      >
        Visuel en cours de génération… (lancer <code className="px-1 mx-1 bg-ink-700 text-foreground rounded">generate_office_phases.py</code>)
      </div>
      <div className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center justify-between gap-2 px-2 py-1 rounded bg-ink-900/80 backdrop-blur-sm">
        <div className="flex items-center gap-1.5 text-[10px] text-slate-300 font-mono">
          <Sparkles className="w-3 h-3 text-cyan-300" />
          <span>{label}</span>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-slate-400 font-mono">
          {[0, 1, 2, 3].map(p => (
            <span
              key={p}
              className={`w-1.5 h-1.5 rounded-full ${p <= phase ? 'bg-cyan-300' : 'bg-slate-700'}`}
              aria-hidden="true"
            />
          ))}
          <span className="ml-1 text-slate-500">{phase}/3</span>
        </div>
      </div>
    </div>
  );
}
